import type { Dispatch, SetStateAction } from 'react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useGuestSiteController } from './GuestSite.controller';
import type { Location } from 'history';
import { AnalyticsContext, useLocationSelector } from '@shared/core';
import { BrannanNavigation, DesignLayoutType, EventPageVisibility, PageKind } from '@graphql/generated-base';
import { ScrollToProvider } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/providers/ScrollToProvider/ScrollToProvider';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import type { StaticContext } from '@react-router';
import { Box, useDisclosure } from '@withjoy/joykit';
import JustFonts from '@apps/guest/packages/layout-engine/components/JustFonts';
import { GuestSiteDebugger } from '@apps/guest/components/GuestSiteDebugger';
import { ActionBar } from '@apps/guest/packages/layout-engine/components/ActionBar';
import { EventGroupCodeBannerOffsetProvider } from '@apps/guest/packages/layout-engine/components/EventGroupCodeBanner';
import { useIsAdminGuestSitePreview } from '@apps/guest/shared/hooks/useIsAdminGuestSitePreview';
import { UnlockDialogProvider } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import { GuestIdentificationDialogProvider } from '@apps/guest/packages/layout-engine/components/GuestIdentificationDialog';
import { EventPersonProvider } from '@apps/guest/shared/eventPerson/EventPersonProvider';
import { AutoUnlockGate } from '@apps/guest/packages/layout-engine/components/UnlockDialog/AutoUnlockGate';
import { LayoutProvider } from '@shared/context/LayoutProvider';
import { wrapWithGuestSiteHealthProvider } from './GuestSite.health';
import { GuestSiteFeatureSet } from '@apps/guest/packages/layout-engine/components/GuestSiteFeatureSet/GuestSiteFeatureSet';
import { useNativeState } from '@shared/core/native';
import { useInformParentOnMousedown } from './hooks/useInformParentOnMousedown';
import { EventDesignThemingProvider } from '@shared/core/website/theming';
import { EventDesignJoyKitThemeOverridesProvider } from '@shared/core/website/theming/EventDesignJoyKitThemeOverridesProvider';
import { ColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { usePurchaseContextIdFromLocation } from '@apps/registry/common/utils/dropship';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import { useGuestSiteUnlockState } from '@apps/guest/shared/hooks/useGuestSiteUnlockState';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { DiscountsProvider } from '@shared/components/DiscountsProvider/DiscountsProvider';
import { HotelsDiscountsProvider } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { UnpublishedWebsite } from './components/UnpublishedWebsite/UnpublishedWebsite';
import { StickyNavBarOffsetProvider } from '@apps/guest/packages/layout-engine/components/StickyNavBarOffset';
import { WebsiteBannerProvider, useWebsiteBannerOffset } from '@shared/components/WebsiteBanner';
import { LazyEmailVerificationDialog } from '@shared/components/EmailVerificationDialog/EmailVerificationDialog.lazy';
import { LazyStationeryCrossSellDialog } from '@shared/components/StationeryCrossSell';
import { LazyGoLiveChecksIntroHost } from './components/GoLiveChecksIntroHost/GoLiveChecksIntroHost.lazy';
import { GUEST_SITE_START_PORTAL_ID } from './GuestSite.constants';
import { GuestSiteHead } from './GuestSiteHead';

import { StatusBanner } from '@shared/components/StatusBanner/StatusBanner';
import { MarriottRoomBlockBannerContainer } from './components/MarriottRoomBlockBanner/MarriottRoomBlockBannerContainer';
import { LazyLayoutAloha } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.lazy';
import { LazyLayoutBrannan } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannan.lazy';
import { LazyLayoutBrannanSinglePage } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannanSinglePage.lazy';
import { LazyPreviewListener } from './PreviewListener.lazy';
import { TemplatePreviewProvider } from './TemplatePreviewContext';
import { AccommodationsLayoutOverrideProvider } from './AccommodationsLayoutOverrideContext';
import { PhotoGalleryLayoutOverrideProvider } from './PhotoGalleryLayoutOverrideContext';
import { LazyAnnouncement } from '@apps/guest/packages/layout-engine/components/Announcement/Announcement.lazy';
import { LazyPurchaseConfirmationDialog } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/components/PurchaseConfirmationDialog/PurchaseConfirmationDialog.lazy';
import { RichTextStylesProvider } from '@apps/guest/packages/layout-engine/widgets/Custom/components/RichText/RichTextStylesProvider';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { LazyWelcomePagePasswordGate } from '@apps/guest/packages/layout-engine/components/WelcomePagePasswordGate/WelcomePagePasswordGate.lazy';
import { LazyWelcomePageOtpGate } from '@apps/guest/packages/layout-engine/components/WelcomePageOtpGate/WelcomePageOtpGate.lazy';
import { PageContentVisibilityProvider } from '@apps/guest/packages/layout-engine/components/PageContentVisibilityProvider';
import { usePrefetchSsrQueries } from './hooks/usePrefetchSsrQueries';
import { useIsStaysRedesignActive } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/useIsStaysRedesignActive';
import { useFetchCustomPageData } from '@apps/guest/packages/layout-engine/widgets/Custom/useFetchCustomPageData';
import { useFetchAccommodationsSsrData } from '@apps/guest/packages/layout-engine/widgets/Custom/useFetchAccommodationsSsrData';
import { computeShouldRenderOtpGate } from './gateDecision';
import { ChunkErrorBoundary } from '@shared/components/ChunkErrorBoundary';

// Layout-level prefetches; runs above the `event && layout` gate so queries fire concurrently with EventWebsiteGuest.
const PrefetchSsrQueriesRenderer: React.FC<{ eventHandle: string }> = ({ eventHandle }) => {
  usePrefetchSsrQueries({ eventHandle });
  return null;
};

// Conditional mount keeps the GuestEventHotelRoomBlocks observer off non-redesign events —
// controller's `refetchQueries({ include: refetchPageQueries.accommodations })` matches by
// operation name and would bypass the hook's internal `skip`.
const HoneymoonAccommodationsPrefetchInner: React.FC<{ eventHandle: string; eventId: string | undefined }> = ({ eventHandle, eventId }) => {
  const { data: pageContainerData } = useFetchCustomPageData(eventHandle, false);
  useFetchAccommodationsSsrData({ eventId, pageContainerData });
  return null;
};

const HoneymoonAccommodationsPrefetchRenderer: React.FC<{ eventHandle: string; eventId: string | undefined }> = ({ eventHandle, eventId }) => {
  const isStaysRedesignActive = useIsStaysRedesignActive();
  if (!isStaysRedesignActive) return null;
  return (
    <HoneymoonAccommodationsPrefetchInner
      eventHandle={eventHandle}
      eventId={eventId}
    />
  );
};

export interface GuestSiteLayoutProps
  extends Readonly<{
    eventHandle: string;
    event: GuestSiteEvent;
    layout: DesignLayoutType;
    setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
    setIsLastPageVisible?: (value: boolean) => void;
    setIsPastFirstPage?: (value: boolean) => void;
    isNativeBrannanOverridePage: boolean;
    hideNav: boolean;
  }> {}

/**
 * Memoised deliberately, and this is the only boundary between the guest site and ~85 providers
 * above it. Anything up there that re-renders — the Router on any URL change, AuthProvider (which
 * subscribes to the route to read the event handle), this component's own parent — otherwise
 * re-rendered every section of the site, which on a 9-page event is ~2,100 components per commit.
 *
 * Every prop here is stable by construction: strings, booleans, an enum, the Apollo event object,
 * and useState setters. So a bail-out means "nothing this subtree renders from has changed", not
 * "we skipped an update". Data still flows in through context and Apollo as before.
 */
const GuestSiteLayout = React.memo<GuestSiteLayoutProps>(
  ({ eventHandle, event, layout, setLoadingTheme, setIsLastPageVisible, setIsPastFirstPage, isNativeBrannanOverridePage, hideNav }) => {
    const brannanNavigation = event.eventDesign?.brannanNavigation;
    const layoutSwitch = () => {
      const layoutTypes = {
        [DesignLayoutType.aloha]: LazyLayoutAloha,
        [DesignLayoutType.brannan]: !isNativeBrannanOverridePage && brannanNavigation === BrannanNavigation.singlePage ? LazyLayoutBrannanSinglePage : LazyLayoutBrannan,
      };
      const Layout = layoutTypes[layout];

      return (
        (event && (
          <ChunkErrorBoundary section={`guest/layout/${layout}`}>
            <Layout
              setLoadingTheme={setLoadingTheme}
              setIsLastPageVisible={setIsLastPageVisible}
              setIsPastFirstPage={setIsPastFirstPage}
              eventHandle={eventHandle}
              event={event}
              hideNav={hideNav}
            />
          </ChunkErrorBoundary>
        )) ||
        null
      );
    };

    return layoutSwitch();
  },
);

GuestSiteLayout.displayName = 'GuestSiteLayout';

/**
 * The only thing this component needs from the URL: which purchase-confirmation dialog the current
 * page would show, or undefined if it is not one of those pages. A boolean-ish selection rather than
 * the whole location, so Aloha's scroll spy does not re-render the entire guest site on every step.
 *
 * Matched on a whole path segment rather than by substring: a wedding site whose handle contains
 * "travel" — `/traveling-together` — matched `.includes('/travel')` on every one of its pages, so a
 * link carrying `pcId` opened the purchase dialog and logged the wrong page type.
 */
const TRAVEL_PATH = /\/travel(\/|$)/;
// `accommodations?` because the canonical slug is plural (ACCOMMODATIONS_SLUG); the singular is
// accepted too, which is what the previous substring test matched.
const ACCOMMODATION_PATH = /\/accommodations?(\/|$)/;

export const selectPurchaseConfirmationPageType = ({ pathname }: Pick<Location, 'pathname'>): 'travel' | 'accommodation' | undefined =>
  TRAVEL_PATH.test(pathname) ? 'travel' : ACCOMMODATION_PATH.test(pathname) ? 'accommodation' : undefined;

export interface GuestSiteBodyProps
  extends Readonly<{
    eventHandle: string;
    staticContext: StaticContext | undefined;
  }> {}

const GuestSiteBodyBase: React.FC<GuestSiteBodyProps> = wrapWithGuestSiteHealthProvider(({ eventHandle, staticContext }) => {
  const {
    event,
    layout,
    eventPageRoutes,
    isPreviewing,
    handleOnPreviewUpdate,
    handleOnRefetchData,
    justFonts,
    graphicAccent,
    shouldRenderActionBar,
    setLoadingTheme,
    setIsPastFirstPage,
    showAnnouncement,
    onAnnouncementDismiss,
    isNativeBrannanOverridePage,
    showUnpublishedPage,
    suppressGuestSiteUntilAuth,
    loadingGuestSite,
    isLoadingWebsitePublishCapability,
    emailVerificationDialogProps,
    crossSellDialogProps,
    isGoLiveChecksIntroOpen,
    closeGoLiveChecksIntro,
  } = useGuestSiteController({
    eventHandle,
    staticContext,
  });
  const { isNative } = useNativeState();
  const isFullySsrEnabled = useIsGuestSiteSsrEnabled();
  // PLAN-17 step-2: unlock state no longer rides on EventWebsiteGuest. While the unlock query
  // is in flight, gate decisions below treat the state as indeterminate (defer gating) so
  // already-unlocked guests don't get a gate/dialog flash.
  const { hasUnlocked: hasUnlockedState, isUnlockStateLoading } = useGuestSiteUnlockState(eventHandle);
  const { checkIsWelcomePage, loading: isWelcomePageLoading } = useWelcomePage();
  const { currentPageSlug, isCustomPageSlug } = useCurrentPageSlug(eventHandle);

  const purchaseConfirmationPageType = useLocationSelector(selectPurchaseConfirmationPageType);
  const purchaseId = usePurchaseContextIdFromLocation();
  const updateSearchParam = useUpdateSearchParam();
  const analytics = useContext(AnalyticsContext);
  const [isPurchaseConfirmationOpen, setIsPurchaseConfirmationOpen] = useState(false);
  const { isOpen: isPurchaseConfirmationDialogOpen, onClose: onPurchaseConfirmationClose } = useDisclosure({
    isOpen: isPurchaseConfirmationOpen,
    onClose: () => {
      updateSearchParam('pcId', null);
      setIsPurchaseConfirmationOpen(false);
    },
  });
  // Get website banner offset
  const { bannerOffset } = useWebsiteBannerOffset();
  // Narrow preview signal — deliberately not `isPreviewing` above, which is bare `isInIframe()`.
  const isAdminGuestSitePreview = useIsAdminGuestSitePreview();

  const shouldRenderPurchaseConfirmationDialog = !!purchaseId && !!purchaseConfirmationPageType;

  useEffect(() => {
    if (shouldRenderPurchaseConfirmationDialog) {
      setIsPurchaseConfirmationOpen(true);
      analytics.track({
        category: 'guest.purchase',
        action: 'PageViewed',
        extraInfo: { name: 'registryPurchaseConfirmation', pageType: purchaseConfirmationPageType },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRenderPurchaseConfirmationDialog]);

  // hook to inform irame parent on mousedown
  useInformParentOnMousedown(isPreviewing);

  const hasUnlocked = hasUnlockedState ?? false;
  const isRootRoute = !currentPageSlug;
  const welcomePage = useMemo(() => {
    const welcomePages = event?.pages?.filter(page => checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);

    if (!welcomePages?.length) {
      return undefined;
    }

    if (isRootRoute) {
      return welcomePages[0];
    }

    if (isCustomPageSlug) {
      // CORE-6470: prefer the custom (non-legacy) welcome row when the URL targets /page/<slug>.
      return welcomePages.find(page => page.pageKind !== PageKind.systemLegacy) ?? welcomePages[0];
    }

    return welcomePages.find(page => page.pageKind === PageKind.systemLegacy) ?? welcomePages[0];
  }, [checkIsWelcomePage, event?.pages, isCustomPageSlug, isRootRoute]);
  const isWelcomeLocked = !!welcomePage && (welcomePage.visibility === EventPageVisibility.passwordProtected || welcomePage.private);
  // When all pages are password-protected, the backend returns an empty pages
  // array for non-admin users who haven't unlocked. Detect this so we can
  // still show the gate on potential welcome routes.
  const allPagesFilteredByPassword = !hasUnlocked && Array.isArray(event?.pages) && event?.pages.length === 0;
  // Defer gate resolution only on routes that can render the welcome page.
  const isPotentialWelcomeRoute = isRootRoute || currentPageSlug === EVENT_PAGE_SLUGS.Welcome;
  const isWelcomeRouteLoading = !isPreviewing && isPotentialWelcomeRoute && isWelcomePageLoading;
  const isOnWelcomeRoute = useMemo(() => {
    if (!welcomePage || isWelcomeRouteLoading) {
      return false;
    }

    if (isRootRoute) {
      return true;
    }

    if (currentPageSlug !== welcomePage.pageSlug) {
      return false;
    }

    return isCustomPageSlug ? welcomePage.pageKind !== PageKind.systemLegacy : welcomePage.pageKind === PageKind.systemLegacy;
  }, [currentPageSlug, isCustomPageSlug, isRootRoute, isWelcomeRouteLoading, welcomePage]);
  const shouldHideNav = !isPreviewing && !isUnlockStateLoading && (isWelcomeLocked || allPagesFilteredByPassword) && !hasUnlocked;
  const shouldRenderWelcomeGate =
    !isPreviewing &&
    !isWelcomeRouteLoading &&
    !isUnlockStateLoading &&
    !hasUnlocked &&
    ((isWelcomeLocked && isOnWelcomeRoute) || (allPagesFilteredByPassword && isPotentialWelcomeRoute));
  // Pure helper — see gateDecision.ts for the rationale. Site-wide (no
  // welcome-route scope) so deep-link routes don't fall through to the
  // legacy AutoUnlockGate dialog.
  const shouldRenderOtpGate =
    !isUnlockStateLoading &&
    computeShouldRenderOtpGate({
      isPreviewing,
      hasUnlocked,
      requiresGuestIdentification: event?.settings?.requiresGuestIdentification ?? false,
      allPagesFilteredByPassword,
    });
  const shouldRenderFullPageGate = shouldRenderWelcomeGate || shouldRenderOtpGate;
  // A gate may be required (locked welcome, fully-filtered pages, or OTP identification)
  // but unlock state is still indeterminate. Hold the layout via loadingGuestSite rather
  // than falling through: rendering the layout would flash a blank no-unlock-affordance
  // state at locked visitors, and rendering the gate would flash it at unlocked ones.
  const isGateResolutionPending =
    !isPreviewing && isUnlockStateLoading && (isWelcomeLocked || allPagesFilteredByPassword || (event?.settings?.requiresGuestIdentification ?? false));

  // Stacked layouts (Aloha, Brannan single-page) defer non-target chrome so
  // the initial paint focuses on the deep-linked route. Brannan classic
  // short-circuits — renders one page per URL so there's nothing stacked to
  // hide, and a classic nav click otherwise gets trapped behind the initial-
  // slug ref until the fallback timer fires.
  //
  // `isNativeBrannanOverridePage` (e.g. native accommodations / things-to-do
  // on a single-page Brannan event) forces the classic Brannan render even
  // when brannanNavigation === singlePage — mirrors the switch in
  // GuestSiteLayout above — so treat it as "classic" here too.
  //
  // Computed here (before event/layout resolve) so `PageContentVisibilityProvider`
  // can mount at the very top of the return — `PrefetchSsrQueriesRenderer`
  // then fires layout-level queries in parallel with `EventWebsiteGuest`
  // instead of waiting behind the `event && layout` gate. Once `layout`
  // resolves, the provider's effect picks up the new `isInitiallyRevealed`
  // value and flips `allRevealed` accordingly.
  // Native app WebView has its own splash overlay managing content reveal;
  // skip the web-side deferred visibility pipeline entirely so stalled
  // progress/onPageFinished events cannot leave content at opacity: 0.
  const isInitiallyRevealed =
    isNative || (layout === DesignLayoutType.brannan && (isNativeBrannanOverridePage || event?.eventDesign?.brannanNavigation !== BrannanNavigation.singlePage));

  return (
    <PageContentVisibilityProvider
      eventHandle={eventHandle}
      pages={event?.pages}
      isInitiallyRevealed={isInitiallyRevealed}
    >
      <PrefetchSsrQueriesRenderer eventHandle={eventHandle} />
      <HoneymoonAccommodationsPrefetchRenderer
        eventHandle={eventHandle}
        eventId={event?.id}
      />
      {/* Only render GuestSiteHead when full SSR is enabled — when disabled,
          GuestSiteHeadCsr in GuestSite.tsx handles SEO meta tags */}
      {isFullySsrEnabled && <GuestSiteHead event={event} />}
      {/* Only the registry's banner: the wedding-code banner reserves its own space in flow via
          EventGroupCodeBannerSlot, so adding its height here would double-reserve. */}
      <Box paddingTop={bannerOffset}>
        {/* Nothing rendered above EventGroupCodeBannerSlot may take flow height: the fixed wedding-code
            banner is pinned at `bannerOffset` and only lands on its slot if the slot is first in flow. */}
        <StatusBanner />
        {emailVerificationDialogProps.isOpen && <LazyEmailVerificationDialog {...emailVerificationDialogProps} />}
        {crossSellDialogProps.isOpen && <LazyStationeryCrossSellDialog {...crossSellDialogProps} />}
        {/* Mounted only once a couple clicks Publish on their own unpublished site: guests never do. */}
        {isGoLiveChecksIntroOpen && <LazyGoLiveChecksIntroHost onDone={closeGoLiveChecksIntro} />}
        <GuestSiteFeatureSet
          loadingGuestSite={loadingGuestSite || isLoadingWebsitePublishCapability || isWelcomeRouteLoading}
          holdForGateResolution={isGateResolutionPending}
          staticContext={staticContext}
        >
          {suppressGuestSiteUntilAuth ? null : event && showUnpublishedPage ? (
            <UnpublishedWebsite />
          ) : event && layout ? (
            <LayoutProvider
              layout={layout}
              brannanNavigation={event.eventDesign?.brannanNavigation}
            >
              <EventDesignThemingProvider eventDesign={event.eventDesign}>
                <EventPersonProvider eventId={event.id}>
                  <UnlockDialogProvider>
                    {!isUnlockStateLoading && (
                      <AutoUnlockGate
                        hasPages={event.pages.length > 0}
                        hasUnlocked={hasUnlocked}
                        isPreviewing={isPreviewing}
                        isFullPageGateActive={shouldRenderFullPageGate}
                      />
                    )}
                    <GuestIdentificationDialogProvider isFullPageGateActive={shouldRenderFullPageGate}>
                      <ColorPaletteProvider eventDesign={event.eventDesign}>
                        <EventDesignJoyKitThemeOverridesProvider>
                          <RichTextStylesProvider>
                            {showAnnouncement && !isNative && (
                              <LazyAnnouncement
                                onAnnouncementClosed={() => onAnnouncementDismiss(event.info.announcementBanner.title)}
                                content={<>{event.info.announcementBanner.message}</>}
                                title={event.info.announcementBanner.title}
                                graphicAccent={graphicAccent}
                              />
                            )}
                            {justFonts ? (
                              <JustFonts />
                            ) : shouldRenderOtpGate ? (
                              // Site-wide gate for events requiring guest identification.
                              // Renders on every route while the BE is filtering pages,
                              // so deep links don't bypass the gate via AutoUnlockGate's
                              // fallback legacy dialog. The BE derives `hasUnlocked`
                              // from `hasIdentity` for these events, so the existing
                              // empty-pages signal still triggers gate-mode here.
                              <LazyWelcomePageOtpGate
                                eventId={event.id}
                                eventDisplayName={event.info.eventDisplayName ?? ''}
                              />
                            ) : shouldRenderWelcomeGate ? (
                              <LazyWelcomePagePasswordGate
                                eventId={event.id}
                                eventDisplayName={event.info.eventDisplayName ?? ''}
                              />
                            ) : (
                              <ScrollToProvider>
                                <AccommodationsLayoutOverrideProvider>
                                  <PhotoGalleryLayoutOverrideProvider>
                                    {isPreviewing && (
                                      <LazyPreviewListener
                                        eventHandle={eventHandle}
                                        eventPageRoutes={eventPageRoutes}
                                        eventDesign={event?.eventDesign}
                                        onRefetchData={handleOnRefetchData}
                                        onUpdate={handleOnPreviewUpdate}
                                      />
                                    )}
                                    <StickyNavBarOffsetProvider offset={bannerOffset}>
                                      <div id={GUEST_SITE_START_PORTAL_ID} />
                                      {/* Hide guest-only chrome in the host preview — see useIsAdminGuestSitePreview. */}
                                      {!isAdminGuestSitePreview && (
                                        <MarriottRoomBlockBannerContainer
                                          event={event}
                                          eventHandle={eventHandle}
                                          isSuppressed={isNative || isPreviewing || shouldRenderFullPageGate || isGateResolutionPending}
                                        />
                                      )}
                                      <GuestSiteLayout
                                        setLoadingTheme={setLoadingTheme}
                                        eventHandle={eventHandle}
                                        event={event}
                                        layout={layout}
                                        setIsPastFirstPage={setIsPastFirstPage}
                                        isNativeBrannanOverridePage={isNativeBrannanOverridePage}
                                        hideNav={shouldHideNav}
                                      />
                                    </StickyNavBarOffsetProvider>
                                  </PhotoGalleryLayoutOverrideProvider>
                                </AccommodationsLayoutOverrideProvider>
                              </ScrollToProvider>
                            )}
                          </RichTextStylesProvider>
                        </EventDesignJoyKitThemeOverridesProvider>
                        <GuestSiteDebugger event={event} />
                      </ColorPaletteProvider>
                      {shouldRenderActionBar && !shouldRenderFullPageGate && <ActionBar eventId={event.id} />}
                    </GuestIdentificationDialogProvider>
                  </UnlockDialogProvider>
                </EventPersonProvider>
              </EventDesignThemingProvider>
            </LayoutProvider>
          ) : null}
        </GuestSiteFeatureSet>
      </Box>
      {/* Defer rendering the purchase confirmation dialog because it imports from registry app */}
      {shouldRenderPurchaseConfirmationDialog && (
        <LazyPurchaseConfirmationDialog
          isOpen={isPurchaseConfirmationDialogOpen}
          onClose={onPurchaseConfirmationClose}
        />
      )}
    </PageContentVisibilityProvider>
  );
});

const GuestSiteBody: React.FC<GuestSiteBodyProps> = props => {
  return (
    <DiscountsProvider>
      <HotelsDiscountsProvider>
        <WebsiteBannerProvider>
          <EventGroupCodeBannerOffsetProvider>
            <TemplatePreviewProvider>
              <GuestSiteBodyBase {...props} />
            </TemplatePreviewProvider>
          </EventGroupCodeBannerOffsetProvider>
        </WebsiteBannerProvider>
      </HotelsDiscountsProvider>
    </DiscountsProvider>
  );
};

GuestSiteBody.displayName = 'GuestSiteBody';

export default GuestSiteBody;
